import React from 'react';
import { t } from '../../utils/translations';
import { getFormattedUserName } from '../../utils/cvUtils';

export default function CVTemplateATSModern({ user, projects, language = 'id' }) {
  // Helper function to categorize education
  const categorizeEducation = (education) => {
    const higher = [];
    const k12 = [];
    
    education.forEach(edu => {
      const degree = edu.degree?.toLowerCase() || '';
      const isHigherEd = degree.includes('sarjana') || degree.includes('s1') || 
                        degree.includes('s2') || degree.includes('s3') || 
                        degree.includes('bachelor') || degree.includes('master') || 
                        degree.includes('magister') || degree.includes('phd') || 
                        degree.includes('doktor') || degree.includes('diploma');
      
      if (isHigherEd) {
        higher.push(edu);
      } else {
        k12.push(edu);
      }
    });
    
    return { higher, k12 };
  };

  const { higher: higherEducation, k12: k12Education } = user?.education ? 
    categorizeEducation(user.education) : { higher: [], k12: [] };

  return (
    <div className="bg-white p-8 max-w-[21cm] mx-auto" style={{ fontFamily: 'Arial, sans-serif' }}>
      {/* Header */}
      <div className="border-b-4 border-gray-900 pb-4 mb-5">
        <h1 className="text-3xl font-bold text-black mb-1 text-left">{getFormattedUserName(user) || 'YOUR NAME'}</h1>
        <p className="text-base text-gray-700 mb-2">{user?.job || 'Professional Title'}</p>
        <div className="text-sm text-black flex flex-wrap gap-3">
          {user?.email && <span>{user.email}</span>}
          {user?.phone && <span>{user.phone}</span>}
          {user?.location && <span>{user.location}</span>}
        </div>
      </div>

      {/* Professional Summary */}
      {user?.summary && (
        <div className="mb-5">
          <h2 className="text-base font-bold text-black uppercase mb-2 bg-gray-100 px-3 py-1">
            {t('professionalSummary', language)}
          </h2>
          <p className="text-sm text-black leading-relaxed text-justify px-1 text-justify">{user.summary}</p>
        </div>
      )}

      {/* Core Competencies */}
      {user?.skills && user.skills.length > 0 && (
        <div className="mb-5">
          <h2 className="text-base font-bold text-black uppercase mb-2 bg-gray-100 px-3 py-1">
            {t('coreCompetencies', language)}
          </h2>
          <div className="grid grid-cols-2 gap-2 px-1">
            {user.skills.map((skill, i) => (
              <div key={i} className="flex items-center">
                <span className="w-2 h-2 bg-black mr-2 flex-shrink-0"></span>
                <span className="text-sm text-black">{skill}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Professional Experience */}
      {user?.experience && user.experience.length > 0 && (
        <div className="mb-5">
          <h2 className="text-base font-bold text-black uppercase mb-2 bg-gray-100 px-3 py-1">
            {t('professionalExperience', language)}
          </h2>
          <div className="space-y-3 px-1">
            {user.experience.map((exp, i) => (
              <div key={i}>
                <div className="flex justify-between items-baseline mb-1">
                  <h3 className="text-sm font-bold text-black">{exp.position}</h3>
                  <span className="text-sm text-gray-700">{exp.period}</span>
                </div>
                <p className="text-sm text-gray-700 mb-1">{exp.company}</p>
                {exp.description && (
                  <p className="text-sm text-black text-justify leading-relaxed text-justify">{exp.description}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Education - Higher Education */}
      {higherEducation.length > 0 && (
        <div className="mb-5">
          <h2 className="text-base font-bold text-black uppercase mb-2 bg-gray-100 px-3 py-1">
            {t('higherEducation', language)}
          </h2>
          <div className="space-y-2 px-1">
            {higherEducation.map((edu, i) => (
              <div key={i}>
                <div className="flex justify-between items-baseline">
                  <h3 className="text-sm font-bold text-black">{edu.degree}</h3>
                  <span className="text-sm text-gray-700">{edu.year}</span>
                </div>
                <p className="text-sm text-gray-700">{edu.school || edu.institution}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Education - K-12 */}
      {k12Education.length > 0 && (
        <div className="mb-5">
          <h2 className="text-base font-bold text-black uppercase mb-2 bg-gray-100 px-3 py-1">
            {t('formalEducation', language)}
          </h2>
          <div className="space-y-2 px-1">
            {k12Education.map((edu, i) => (
              <div key={i}>
                <div className="flex justify-between items-baseline">
                  <h3 className="text-sm font-bold text-black">{edu.degree}</h3>
                  <span className="text-sm text-gray-700">{edu.year}</span>
                </div>
                <p className="text-sm text-gray-700">{edu.school || edu.institution}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Key Projects */}
      {projects && projects.length > 0 && (
        <div className="mb-5">
          <h2 className="text-base font-bold text-black uppercase mb-2 bg-gray-100 px-3 py-1">
            {t('keyProjects', language)}
          </h2>
          <div className="space-y-2 px-1">
            {projects.map((proj, i) => (
              <div key={i}>
                <h3 className="text-sm font-bold text-black">{proj.title}</h3>
                <p className="text-sm text-black text-justify leading-relaxed text-justify">{proj.description}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
